import deepEqual from 'deep-equal';

/**
 * Convert PRESTOplay state to a human-readable string.
 *
 * @param {clpp.Player.State} state player state
 * @param {clpp.events.BufferingReasons} changeReason reason for the state change
 * @returns {string}
 */
export function stateToString (state, changeReason) {
  const States = clpp.Player.State;
  switch (state) {
    case States.IDLE:
      return 'IDLE';
    case States.PREPARING:
      return 'PREPARING';
    case States.BUFFERING: {
      const reason = changeReason === clpp.events.BufferingReasons.SEEKING
        ? 'SEEK'
        : 'NO-DATA';
      const label = `BUFFERING-[${reason}]`;
      return label;
    }
    case States.PLAYING:
      return 'PLAYING';
    case States.PAUSED:
      return 'PAUSED';
    case States.ENDED:
      return 'ENDED';
    case States.ERROR:
      return 'ERROR';
  }
  return 'UNKNOWN';
}

export function loadAsset (player, asset) {
  if (!player || !asset || !asset.src) return Promise.resolve();

  const config = Object.assign({}, asset.cfg);

  config.source = asset.src;
  return player.load(config);
}

export function defaultPlayerConfig () {
  const cfg = clpp.utils.PlayerConfiguration.create();
  cfg.metadata = {
    title: ''
  };
  cfg.source = {
    url: '',
    type: null,
    drmProtected: false
  };
  cfg.autoplay = cfg.autoplay || false;
  cfg.thumbnails = cfg.thumbnails || {
    enabled: true,
    preload: []
  };
  cfg.htmlcue = cfg.htmlcue || {};
  cfg.htmlcue.stretchSmpteImage = cfg.htmlcue.stretchSmpteImage || false;
  cfg.htmlcue.enableResizeObserver = cfg.htmlcue.enableResizeObserver || false;

  cfg.ttml = cfg.ttml || {};
  cfg.ima = cfg.ima || {
    adTagUrl: '',
    enablePreloading: true
  };
  cfg.cmcd = cfg.cmcd || {};
  return cfg;
}

export function createPlayerConfig (config, stripDefaults = false) {
  const cfg = clpp.utils.PlayerConfiguration.create(
    defaultPlayerConfig(),
    {
      textStyle: {
        fontFamily: '\'Roboto\', sans-serif',
        fontColor: 'white',
        backgroundColor: 'rgba(0, 0, 0, 0.75)'
      }
    },
    config
  );
  if (!stripDefaults) {
    return cfg;
  }
  const baseConfig = JSON.parse(JSON.stringify(cfg));
  const defaultConfig = defaultPlayerConfig();
  let clean = removeDefaults(defaultConfig, baseConfig);
  if (clean.lowLatencyMode) {
    const llConfig = createPlayerConfig({ lowLatencyMode: !!(config && config.lowLatencyMode) });
    clean = removeDefaults(llConfig, clean);
    clean.lowLatencyMode = true;
  }
  return clean;
}

function isEmpty (obj) {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
}

export function removeDefaults (defaultConfig, customConfig) {
  if (customConfig === null || customConfig === undefined) {
    return customConfig;
  }
  if (defaultConfig === null || defaultConfig === undefined) {
    return isEmpty(customConfig) ? undefined : customConfig;
  }

  const clone = JSON.parse(JSON.stringify(customConfig));

  Object.keys(customConfig).forEach(key => {
    let defaultValue = defaultConfig;
    defaultValue = defaultConfig[key];

    const customValue = customConfig[key];
    if (deepEqual(defaultValue, customValue)) {
      // values are the same so we can remove it from custom
      delete clone[key];
    } else {
      const to = typeof customValue;
      if (to === 'boolean' || to === 'number' || to === 'string' || customValue === null) {
        if (customValue === null && (defaultValue === Infinity || Number.isNaN(defaultValue))) {
          // we use null for serialization, this is also considered the default
          delete clone[key];
        } else {
          if (Number.isNaN(customValue) && Number.isNaN(defaultValue)) {
            delete clone[key];
          } else {
            if (customValue !== undefined) {
              clone[key] = customValue;
            } else {
              delete clone[key];
            }
          }
        }
      } else {
        if (Number.isNaN(customValue) && Number.isNaN(defaultValue)) {
          delete clone[key];
        } else {
          // they are not equal so lets recursively remove
          const result = removeDefaults(defaultValue, customValue);
          if (result !== undefined) {
            clone[key] = result;
          } else {
            delete clone[key];
          }
        }
      }
    }
  });
  return isEmpty(clone) ? undefined : clone;
}

function sourceToUrl (source) {
  if (typeof source === 'string') {
    return source;
  }
  if (typeof source === 'object' && typeof source.url === 'string') {
    return source.url;
  }
  return null;
}

/**
 * Get asset URL from player config, in case of multiple get the first one.
 *
 * @param {*} config player config
 * @returns {string|null} asset URL
 */
export function configToUrl (config) {
  if (!config || !config.source) {
    return null;
  }

  if (Array.isArray(config.source)) {
    return sourceToUrl(config.source[0]);
  } else {
    return sourceToUrl(config.source);
  }
}
